import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Ticket, TicketsState } from '../../interfaces/Ticket';
import { TicketService } from '../../services/tickets/Tickets';

const initialState: TicketsState = {
  tickets: [],
  closedTickets: [],
  departmentTickets: [],
  loading: false,
  error: null,
};

type FetchParams = {
  companyId?: number;
  department?: string;
  status?: string;
  me?: boolean;
};

export const fetchTicketsGeneric = createAsyncThunk<
  Ticket[],
  FetchParams,
  { rejectValue: string }
>(
  'tickets/fetchGeneric',
  async (
    { companyId, department, status },
    { rejectWithValue }
  ) => {
    try {
      const service = new TicketService();

      if (department) {
        const data =
          await service.getTicketsByDepartment(department);

        return Array.isArray(data?.tickets)
          ? data.tickets
          : [];
      }

      if (!companyId) {
        return rejectWithValue('Company ID ontbreekt');
      }

      const data = await service.getTickets(
        companyId,
        undefined,
        undefined,
        status
      );

      return Array.isArray(data?.tickets)
        ? data.tickets
        : [];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch tickets'
      );
    }
  }
);

const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,

  reducers: {
    updateTicket(state, action) {
      const { id } = action.payload;

      const departmentIndex =
        state.departmentTickets.findIndex(
          (ticket) => ticket.tic_id === id
        );

      if (departmentIndex !== -1) {
        state.departmentTickets[departmentIndex] = {
          ...state.departmentTickets[departmentIndex],
          ...action.payload,
        };
      }

      const openIndex = state.tickets.findIndex(
        (ticket) => ticket.tic_id === id
      );

      if (openIndex !== -1) {
        state.tickets[openIndex] = {
          ...state.tickets[openIndex],
          ...action.payload,
        };
      }

      const closedIndex = state.closedTickets.findIndex(
        (ticket) => ticket.tic_id === id
      );

      if (closedIndex !== -1) {
        state.closedTickets[closedIndex] = {
          ...state.closedTickets[closedIndex],
          ...action.payload,
        };
      }
    },

    appendTicket(state, action: PayloadAction<Ticket>) {
      state.departmentTickets.push(action.payload);

      if (action.payload.tic_status === 'Gesloten') {
        state.closedTickets.push(action.payload);
      } else {
        state.tickets.push(action.payload);
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketsGeneric.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(
        fetchTicketsGeneric.fulfilled,
        (state, action) => {
          state.loading = false;
          state.error = null;

          const { department, status } = action.meta.arg;
          const payload = Array.isArray(action.payload)
            ? action.payload
            : [];

          if (department) {
            state.departmentTickets = payload;
            return;
          }

          if (status === 'Gesloten') {
            state.closedTickets = payload.filter(
              (ticket) =>
                ticket.tic_status?.toLowerCase() ===
                'gesloten'
            );

            return;
          }

          /*
           * De gewone endpoint-response bevat blijkbaar
           * zowel open als gesloten tickets.
           */
          state.tickets = payload.filter(
            (ticket) =>
              ticket.tic_status?.toLowerCase() !==
              'gesloten'
          );

          state.closedTickets = payload.filter(
            (ticket) =>
              ticket.tic_status?.toLowerCase() ===
              'gesloten'
          );
        }
      )

      .addCase(fetchTicketsGeneric.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ??
          action.error.message ??
          'Failed to fetch tickets';
      });
  },
});

export const { updateTicket, appendTicket } =
  ticketsSlice.actions;

export default ticketsSlice.reducer;